import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input } from '@angular/core';

type SelectableChipOption = {
  id: string;
  label: string;
};

type ExerciseFormatOption = {
  id: string;
  title: string;
  description: string;
  icon: string;
};

type ObjectiveOption = {
  id: string;
  label: string;
};

@Component({
  selector: 'app-step-three',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './step-three.component.html',
  styleUrl: './step-three.component.scss',
})
export class StepThreeComponent {
  @Input({ required: true }) participantOptions!: SelectableChipOption[];
  @Input({ required: true }) exerciseFormats!: ExerciseFormatOption[];
  @Input({ required: true }) selectedParticipantIds!: Set<string>;
  @Input({ required: true }) selectedExerciseFormatId!: string | null;
  @Input({ required: true }) customExerciseType!: string;
  @Input({ required: true }) objectiveOptions!: ObjectiveOption[];
  @Input({ required: true }) selectedObjectiveIds!: string[];
  @Input({ required: true }) pendingObjectiveIds!: Set<string>;

  objectiveSearchTerm = '';
  isObjectiveDropdownOpen = false;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(target)) {
      this.isObjectiveDropdownOpen = false;
      return;
    }

    if (!target.closest('.objective-controls')) {
      this.isObjectiveDropdownOpen = false;
    }
  }

  get filteredObjectiveOptions(): ObjectiveOption[] {
    const query = this.objectiveSearchTerm.trim().toLowerCase();

    if (!query) {
      return this.objectiveOptions;
    }

    return this.objectiveOptions.filter((objective) =>
      objective.label.toLowerCase().includes(query),
    );
  }

  get hasPendingObjectives(): boolean {
    return this.pendingObjectiveIds.size > 0;
  }

  get canAddCustomObjective(): boolean {
    const normalizedLabel = this.objectiveSearchTerm.trim().toLowerCase();

    return (
      !!normalizedLabel &&
      !this.objectiveOptions.some(
        (objective) => objective.label.toLowerCase() === normalizedLabel,
      )
    );
  }

  get selectedObjectives(): ObjectiveOption[] {
    return this.selectedObjectiveIds
      .map((id) =>
        this.objectiveOptions.find((objective) => objective.id === id),
      )
      .filter((objective): objective is ObjectiveOption => !!objective);
  }

  toggleParticipantSelection(participantId: string): void {
    if (this.selectedParticipantIds.has(participantId)) {
      this.selectedParticipantIds.delete(participantId);
      return;
    }

    this.selectedParticipantIds.add(participantId);
  }

  isParticipantSelected(participantId: string): boolean {
    return this.selectedParticipantIds.has(participantId);
  }

  selectExerciseFormat(formatId: string): void {
    this.selectedExerciseFormatId =
      this.selectedExerciseFormatId === formatId ? null : formatId;
  }

  isExerciseFormatSelected(formatId: string): boolean {
    return this.selectedExerciseFormatId === formatId;
  }

  updateCustomExerciseType(event: Event): void {
    this.customExerciseType = (event.target as HTMLInputElement).value;
  }

  openObjectiveDropdown(): void {
    this.isObjectiveDropdownOpen = true;
  }

  toggleObjectiveDropdown(event: Event): void {
    event.stopPropagation();
    this.isObjectiveDropdownOpen = !this.isObjectiveDropdownOpen;
  }

  updateObjectiveSearch(event: Event): void {
    event.stopPropagation();
    this.objectiveSearchTerm = (event.target as HTMLInputElement).value;
    this.isObjectiveDropdownOpen = true;
  }

  togglePendingObjective(objectiveId: string): void {
    if (this.pendingObjectiveIds.has(objectiveId)) {
      this.pendingObjectiveIds.delete(objectiveId);
      return;
    }

    this.pendingObjectiveIds.add(objectiveId);
  }

  isObjectivePending(objectiveId: string): boolean {
    return this.pendingObjectiveIds.has(objectiveId);
  }

  addCustomObjective(): void {
    const label = this.objectiveSearchTerm.trim();

    if (!label || !this.canAddCustomObjective) {
      return;
    }

    const id = `custom-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    this.objectiveOptions = [...this.objectiveOptions, { id, label }];
    this.pendingObjectiveIds.add(id);
  }

  commitObjectives(): void {
    if (!this.pendingObjectiveIds.size) {
      return;
    }

    const nextIds = this.objectiveOptions
      .filter((objective) => this.pendingObjectiveIds.has(objective.id))
      .map((objective) => objective.id)
      .filter(
        (objectiveId) => !this.selectedObjectiveIds.includes(objectiveId),
      );

    this.selectedObjectiveIds = [...this.selectedObjectiveIds, ...nextIds];
    this.pendingObjectiveIds.clear();
    this.objectiveSearchTerm = '';
    this.isObjectiveDropdownOpen = false;
  }

  removeSelectedObjective(objectiveId: string): void {
    this.selectedObjectiveIds = this.selectedObjectiveIds.filter(
      (selectedId) => selectedId !== objectiveId,
    );
    this.pendingObjectiveIds.delete(objectiveId);
  }
}
